import { reactive } from 'vue'

export interface FormFloatingActionsConfig {
  submit: () => void | Promise<void>
  cancel?: () => void
  submitLabel?: string
  getSubmitLabel?: () => string
  cancelLabel?: string
  getLoading?: () => boolean
  validate?: () => boolean
  getCanSubmit?: () => boolean
}

export const formFloatingActions = reactive({
  visible: false,
  canSubmit: false,
  submitLabel: '',
  cancelLabel: '',
  loading: false,
  onSubmit: null as (() => void | Promise<void>) | null,
  onCancel: null as (() => void) | null,
  getSubmitLabel: null as (() => string) | null,
  getLoading: null as (() => boolean) | null,
  onValidate: null as (() => boolean) | null,
  refreshCanSubmit: null as (() => void) | null,
})

function applyBodyPadding(visible: boolean): void {
  document.body.classList.toggle('form-floating-actions-visible', visible)
}

function syncCanSubmit(): void {
  formFloatingActions.canSubmit = formFloatingActions.refreshCanSubmit?.() ?? false
}

export function registerFormFloatingActions(config: FormFloatingActionsConfig): void {
  formFloatingActions.visible = true
  applyBodyPadding(true)
  formFloatingActions.onSubmit = config.submit
  formFloatingActions.onCancel = config.cancel ?? null
  formFloatingActions.submitLabel = config.submitLabel ?? ''
  formFloatingActions.cancelLabel = config.cancelLabel ?? ''
  formFloatingActions.getSubmitLabel = config.getSubmitLabel ?? null
  formFloatingActions.getLoading = config.getLoading ?? null
  formFloatingActions.onValidate = config.validate ?? null
  formFloatingActions.refreshCanSubmit = config.getCanSubmit
    ? () => {
        formFloatingActions.canSubmit = config.getCanSubmit!()
      }
    : null
  formFloatingActions.loading = false
  syncCanSubmit()
}

export function showFormFloatingActions(config: FormFloatingActionsConfig): void {
  registerFormFloatingActions(config)
}

export function hideFormFloatingActions(): void {
  formFloatingActions.visible = false
  formFloatingActions.canSubmit = false
  applyBodyPadding(false)
  formFloatingActions.onSubmit = null
  formFloatingActions.onCancel = null
  formFloatingActions.getSubmitLabel = null
  formFloatingActions.getLoading = null
  formFloatingActions.onValidate = null
  formFloatingActions.refreshCanSubmit = null
  formFloatingActions.loading = false
}

export function refreshFormFloatingCanSubmit(): void {
  syncCanSubmit()
}

export async function triggerFormFloatingSubmit(): Promise<void> {
  if (!formFloatingActions.onSubmit || formFloatingActions.loading) return

  const externalLoading = formFloatingActions.getLoading?.()
  if (externalLoading) return

  formFloatingActions.loading = true
  try {
    await formFloatingActions.onSubmit()
  } finally {
    formFloatingActions.loading = false
    syncCanSubmit()
  }
}

export function triggerFormFloatingCancel(): void {
  const onCancel = formFloatingActions.onCancel
  hideFormFloatingActions()
  onCancel?.()
}
